import { computed, ref } from 'vue'
import { isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import { useActiveStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useActiveStatus'
import { toProviderStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/providerData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { BASES_AUTOCOMPLETE_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'
import {
  SUBCONTRACTORS_ENDPOINT,
  SUBCONTRACTORS_TABLE_KEY,
  SUBCONTRACTORS_TRANSLATION_NAMESPACE as NS,
  SUBCONTRACTORS_UI_VIEW,
  SUBCONTRACTOR_DETAILS_ROUTE
} from '@/modules/road-maintenance-module/winter-service-v2/subcontractors/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: SUBCONTRACTORS_ENDPOINT
  }
}

export function useTable (options) {
  const {
    trans,
    router,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  const { statusCellConfig, buildStatusCell } = useActiveStatus({ trans })

  const isCreating = ref(false)

  const startCreating = () => {
    isCreating.value = true
  }

  const resetCreating = () => {
    isCreating.value = false
  }

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[SUBCONTRACTORS_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onShowSubcontractorDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: SUBCONTRACTOR_DETAILS_ROUTE,
      params: {
        id: String(id)
      }
    })
  }

  const deleteSubcontractor = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      setInfoDialogLoaderFn(true)
      await api(WINTER_SERVICE_MODULE).delete(`${SUBCONTRACTORS_ENDPOINT}/${id}`)
      closeInfoDialogFn()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onSubcontractorDelete = (event) => {
    openInfoDialogFn({
      title: trans(`${NS}.delete_confirmation_title`),
      message: trans(`${NS}.delete_confirmation_message`),
      onConfirm: () => deleteSubcontractor(event)
    })
  }

  // The listing reports a subcontractor nested - the address sits on `geolocation`,
  // the contact on `responsible_person`, the base on `base` - so the columns read
  // their value off those instead of a flat key.
  const cellsConfig = computed(() => ({
    default_base: {
      custom_display_value: (data) => data?.base?.name || ''
    },
    address: {
      custom_display_value: (data) => data?.geolocation?.address || ''
    },
    responsible_person: {
      custom_display_value: (data) => data?.responsible_person?.name || ''
    },
    status: {
      ...statusCellConfig,
      custom_display_value: (data) => buildStatusCell(toProviderStatus(data))
    }
  }))

  const headers = computed(() => ([
    {
      label: trans(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: trans(`${NS}.default_base`),
      key: 'default_base',
      always_visible: true
    },
    {
      label: trans(`${NS}.company_name`),
      key: 'name',
      always_visible: true
    },
    {
      label: trans(`${NS}.oib`),
      key: 'oib',
      always_visible: true
    },
    {
      label: trans(`${NS}.address`),
      key: 'address',
      always_visible: true
    },
    {
      label: trans(`${NS}.responsible_person`),
      key: 'responsible_person',
      always_visible: true
    },
    {
      label: trans(`${NS}.operator_count`),
      key: 'operator_count',
      always_visible: true
    },
    {
      label: trans(`${NS}.status`),
      key: 'status',
      always_visible: true
    }
  ]))

  // Only the filters the listing endpoint actually understands - name, oib and the
  // base the subcontractor works out of.
  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: trans(`${NS}.company_name`)
      },
      {
        key: 'oib',
        label: trans(`${NS}.oib`)
      },
      {
        key: 'base_id',
        label: trans(`${NS}.default_base`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: BASES_AUTOCOMPLETE_ENDPOINT
        }
      }
    ]
  }))

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowSubcontractorDetails),
    deleteAction(trans, onSubcontractorDelete)
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.subcontractors')
      },
      {
        text: trans(`${NS}.overview`),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: SUBCONTRACTORS_TABLE_KEY,
      uiView: SUBCONTRACTORS_UI_VIEW,
      apiConfig: API_CONFIG,
      rowDblClick: onShowSubcontractorDetails,
      search_and_buttons_config: {
        // Free text search over the company name and the OIB - the filters dialog
        // stays for the narrower, per-field lookups.
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        showNewBtn: true,
        newClick: startCreating,
        newButtonConfig: {
          label: trans(`${NS}.new_subcontractor`)
        }
      },
      // Refresh, filters, columns visibility and the table/grid switcher are all
      // on by default - buildConfig fills them in.
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    isCreating,
    resetCreating,
    fetchTableData
  }
}
